import { DraggableLocation, DropResult } from '@hello-pangea/dnd';
import { dropHandler, isSamePosition, isValidDrop } from '@/utils/dragdrop';
import { BANK_ID, SlotConfig } from './types';

export type StringPlacement = Record<string, string[]>;

type ResolvePlacementArgs = {
  placement: StringPlacement;
  slots: SlotConfig[];
  result: DropResult;
};

const insertAt = (items: string[], item: string, index: number) => {
  const next = [...items];
  next.splice(index, 0, item);
  return next;
};

const withoutToken = (items: string[] = [], token: string) =>
  items.filter(current => current !== token);

export const containerOf = (placement: StringPlacement, id: string) =>
  Object.keys(placement).find(container =>
    (placement[container] ?? []).includes(id),
  );

const assignToCapacityOneSlot = (
  placement: StringPlacement,
  token: string,
  slotId: string,
  source: DraggableLocation,
): StringPlacement => {
  const next = { ...placement };

  next[source.droppableId] = withoutToken(next[source.droppableId], token);

  const tokensFromSlot = next[slotId] ?? [];

  const occupant = tokensFromSlot.at(0);
  if (occupant !== undefined && occupant !== token) {
    next[source.droppableId] = insertAt(
      next[source.droppableId] ?? [],
      occupant,
      source.index,
    );
  }

  next[slotId] = [token];
  return next;
};

export const capacityOf = (slots: SlotConfig[], slotId: string) => {
  if (slotId === BANK_ID) return 'multiple';
  return slots.find(slot => slot.id === slotId)?.capacity ?? 1;
};

export function resolvePlacement({
  placement,
  slots,
  result,
}: ResolvePlacementArgs): StringPlacement {
  if (!isValidDrop(result) || isSamePosition(result)) return placement;

  const { draggableId: token, source, destination } = result;
  const sourceId = source.droppableId;
  const destinationId = destination.droppableId;

  const capacityOfDestination = capacityOf(slots, destinationId);

  if (sourceId === destinationId) {
    if (capacityOfDestination !== 'multiple') return placement;
    return {
      ...placement,
      [destinationId]: dropHandler(result, placement[destinationId] ?? []),
    };
  }

  if (capacityOfDestination === 'multiple') {
    const next = { ...placement };
    next[sourceId] = withoutToken(next[sourceId], token);
    next[destinationId] = insertAt(
      next[destinationId] ?? [],
      token,
      destination.index,
    );
    return next;
  }

  return assignToCapacityOneSlot(placement, token, destinationId, source);
}
